import {defineStore} from 'pinia';
import type {Widget, WidgetItemButton} from '~~/api/models';
import {useUserStore} from './user';
import {useCookie, useNuxtApp} from '#imports';
import {useItemStore} from './item';
import {useItemSelectionStore} from './itemSelection';

export const useWidgetStore = defineStore('widget', {
  state: () => ({
    widgets: [] as Widget[],
    activeWidget: null as Widget | null,
    widgetLoading: true
  }),

  actions: {
    cleanWidgetStore() {
      this.$reset();

      try {
        useCookie('widget').value = null;
      } catch (e) {
        console.error(e);
      }
    },

    async getWidgets(refresh: boolean = false) {
      if (!refresh && this.widgets.length > 0) return this.widgets;

      const {$apiService} = useNuxtApp();
      const userStore = useUserStore();
      const serialNumbers = userStore.serialNumbers;

      const widgets = await $apiService.Widget.getWidgets();
      if (serialNumbers != null) {
        const counts: Record<string, number> = await $apiService.Widget.getWidgetCounts(serialNumbers as string[]);

        Object.entries(counts).forEach(([widgetName, count]) => {
          const widget = widgets.find(widget => widget.normalizedName === widgetName);
          if (widget)
            widget.itemCount = count;
        });
      }

      this.setWidgets(widgets);

      return this.widgets;
    },

    async getWidgetCounts(serialNumber:string | null = null) {
      const {$apiService} = useNuxtApp();
      const userStore = useUserStore();
      let serialNumbers = null;

      if(serialNumber != null){
        serialNumbers = [serialNumber];
      }else{
        serialNumbers = userStore.serialNumbers;
      }

      if (serialNumbers != null) {
        const counts: Record<string, number> = await $apiService.Widget.getWidgetCounts(serialNumbers as string[]);
        Object.entries(counts).forEach(([widgetName, count]) => {
          const widget = this.widgets.find(widget => widget.normalizedName === widgetName);
          if (widget)
            widget.itemCount = count;
        });
      }
    },

    setWidgets(widgets: Widget[]) {
      this.widgets = widgets.filter(widget => !widget.archive);
      this.archiveWidgets = widgets.filter(widget => widget.archive);
    },

    async getItemCount(widget: Widget) {
      const {$apiService} = useNuxtApp();
      const userStore = useUserStore();
      const serialNumbers = userStore.serialNumbers;

      if (serialNumbers != null && widget.id != null)
        widget.itemCount = await $apiService.Widget.getWidgetCount(serialNumbers as string[], widget.normalizedName);
    },

    setActiveWidget(widget: Widget) {
      const itemSelectionStore = useItemSelectionStore();
      const {$apiService} = useNuxtApp();

      // If the widget is different from the previous widget, clear the selected items
      if (widget !== this.activeWidget)
        itemSelectionStore.clearSelectedItems();

      // Update the active widget in the store
      this.activeWidget = widget;
      $apiService.activeWidget = widget;


      return this.activeWidget;
    },

    setActiveWidgetRoute(widget: Widget) {
      this.widgetLoading = true;
      const itemStore = useItemStore();
      itemStore.setLoadingItems(true);

      if (!widget) return;

      this.setActiveWidget(widget);

      // Update the route with the widget's normalized name
      if (widget.normalizedName && import.meta.client) {
        navigateTo({
          name: 'documents-widget', // Route name defined by the file name
          params: {widget: widget.normalizedName}, // Pass the normalized name of the widget
          force: true
        });
        setTimeout(() => {
          this.setWidgetLoading(false);
        }, 450);
      }
    },

    setWidgetLoading(value: boolean) {
      this.widgetLoading = value;
    },

    activeWidgetHasItemButton(buttonName: string): boolean {
      try {
        const itemButtons = this.activeWidget?.itemButtons;
        const searchedButton = buttonName.toLowerCase();

        // Helper function for recursive sub-button search
        const hasButton = (buttons: WidgetItemButton[] | null | undefined): boolean => {
          if (!buttons || !Array.isArray(buttons)) {
            return false;
          }

          return buttons.some(button =>
            button.button.toLowerCase() === searchedButton ||
            hasButton(button.subButtons)
          );
        };

        // Check the primary and sub-buttons
        return hasButton(itemButtons);
      } catch (error) {
        console.error(error);
        return false;
      }
    },

    getByNormalizedName(name: string): Widget | undefined {
      return this.widgets.find(widget => widget.normalizedName === name);
    }
  }
});